import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Select,
  Switch,
  Button,
  Spin,
  Space,
  notification,
  Input,
  InputNumber,
} from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WhatsAppOutlined,
  PhoneOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  fetchSettings,
  updateSettings,
  disconnectWhatsapp,
  connectSms,
  disconnectSms,
} from "./service";
import { getIanaTimezoneOptions, suggestBrowserTimezone, CALL_RETENTION_OPTIONS } from "./constants";
import styles from "./style.module.scss";
import { BrandTag } from "pages/SurveyManagement/phoneTriggerShared";

const { TextArea } = Input;

const DEPLOYMENT_MODES = [
  { value: "on_prem", label: "On-prem" },
  { value: "gcp", label: "Google Cloud (GCP)" },
  { value: "aws", label: "AWS" },
];

const TIMEZONE_OPTIONS = getIanaTimezoneOptions();

const SettingsTab = ({ active, focusCompliance }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const complianceRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [smsConnecting, setSmsConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchSettings();
    if (res && !res.error) {
      const s = res.settings || {};
      if (!s.timezone) {
        s.timezone = suggestBrowserTimezone();
      }
      if (!Array.isArray(s.opt_out_keywords) || s.opt_out_keywords.length === 0) {
        s.opt_out_keywords = ["DUR", "STOP"];
      }
      setSettings(s);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  useEffect(() => {
    if (!active || !focusCompliance || !complianceRef.current) return undefined;
    const timer = setTimeout(() => {
      complianceRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(timer);
  }, [active, focusCompliance, settings]);

  const patch = (p) => setSettings((s) => ({ ...s, ...p }));

  const save = async () => {
    setSaving(true);
    const res = await updateSettings({
      deployment_mode: settings.deployment_mode,
      auto_channel_routing_enabled: settings.auto_channel_routing_enabled,
      voice_fallback_for_unanswered_whatsapp:
        settings.voice_fallback_for_unanswered_whatsapp,
      sms_fallback_for_unanswered_whatsapp:
        settings.sms_fallback_for_unanswered_whatsapp,
      sms_fallback_delay_minutes: settings.sms_fallback_delay_minutes,
      timezone: settings.timezone,
      notify_on_sonic_fire: settings.notify_on_sonic_fire,
      notify_on_rule_failure: settings.notify_on_rule_failure,
      consent_text: settings.consent_text,
      opt_out_keywords: settings.opt_out_keywords,
      call_recording_retention_days: settings.call_recording_retention_days,
    });
    setSaving(false);
    if (res && !res.error) {
      notification.success({ message: t("sonic.settings_saved") });
      load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const handleDisconnect = async () => {
    setConnecting(true);
    const res = await disconnectWhatsapp();
    setConnecting(false);
    if (res && !res.error) {
      notification.success({ message: t("sonic.whatsapp_disconnected_ok") });
      if (res.settings) setSettings(res.settings);
      else load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const handleSmsDisconnect = async () => {
    setSmsConnecting(true);
    const res = await disconnectSms();
    setSmsConnecting(false);
    if (res && !res.error) {
      notification.success({ message: t("sonic.sms_disconnected_ok") });
      if (res.settings) setSettings(res.settings);
      else load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const handleSmsConnect = async () => {
    setSmsConnecting(true);
    const res = await connectSms();
    setSmsConnecting(false);
    if (res && !res.error) {
      notification.success({ message: t("sonic.sms_connected_ok") });
      if (res.settings) setSettings(res.settings);
      else load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  if (loading || !settings) {
    return (
      <div className={styles.centeredSpin}>
        <Spin />
      </div>
    );
  }

  const waConnected = !!settings.whatsapp_connected || !!settings.whatsapp_configured;
  const perOrgConnected = !!settings.whatsapp_connected;
  const voiceConnected =
    !!settings.voice_provider_connected ||
    !!(settings.voice_provider_config && settings.voice_provider_config.phone_number);

  return (
    <div className={styles.tabInner}>
      <div className={styles.sonicPanel}>
      <div className={styles.settingsCard}>
        <label className={styles.fieldLabel}>{t("sonic.deployment_mode")}</label>
        <Select
          style={{ width: 280 }}
          value={settings.deployment_mode}
          onChange={(v) => patch({ deployment_mode: v })}
          options={DEPLOYMENT_MODES}
        />
      </div>

      <div className={styles.settingsCard}>
        <div className={styles.toggleTitle}>{t("sonic.waba_connection")}</div>
        <div className={styles.wabaStatus}>
          {waConnected ? (
            <BrandTag tone="purple">
              <CheckCircleOutlined /> {t("sonic.connected")}
            </BrandTag>
          ) : (
            <BrandTag tone="pink">
              <ExclamationCircleOutlined /> {t("sonic.not_connected")}
            </BrandTag>
          )}
          {settings.whatsapp_phone_number_id && (
            <span className={styles.wabaId}>
              {t("sonic.phone_number_id")}: {settings.whatsapp_phone_number_id}
            </span>
          )}
        </div>
        <div className={styles.wabaActions}>
          {perOrgConnected ? (
            <Button danger loading={connecting} onClick={handleDisconnect}>
              {t("sonic.disconnect")}
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<WhatsAppOutlined />}
              onClick={() => history.push("/settings/integrations")}
            >
              {t("sonic.connect_whatsapp")}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.settingsCard}>
        <div className={styles.toggleTitle}>{t("sonic.voice_integration")}</div>
        <div className={styles.wabaStatus}>
          {voiceConnected ? (
            <BrandTag tone="purple">
              <CheckCircleOutlined /> {t("sonic.connected")}
            </BrandTag>
          ) : (
            <BrandTag tone="pink">
              <ExclamationCircleOutlined /> {t("sonic.not_connected")}
            </BrandTag>
          )}
          <PhoneOutlined className={styles.settingsPhoneIcon} />
          <span className={styles.wabaId}>
            {settings.voice_provider_config?.phone_number ||
              t("sonic.voice_number_not_set")}
          </span>
        </div>
      </div>

      <div className={styles.settingsCard}>
        <label className={styles.fieldLabel}>{t("sonic.timezone")}</label>
        <Select
          showSearch
          style={{ width: 360 }}
          value={settings.timezone || "UTC"}
          onChange={(v) => patch({ timezone: v })}
          options={TIMEZONE_OPTIONS}
          optionFilterProp="label"
          placeholder={t("sonic.timezone_search_placeholder")}
        />
        <p className={styles.fieldHelper}>
          {t("sonic.timezone_desc")}{" "}
          {t("sonic.timezone_browser_hint", { tz: suggestBrowserTimezone() })}
        </p>
      </div>

      <div className={styles.settingsCard}>
        <div className={styles.toggleTitle}>{t("sonic.notification_prefs")}</div>
        <div className={styles.toggleRow} style={{ marginTop: 12 }}>
          <div>
            <div className={styles.toggleDesc}>{t("sonic.notify_on_fire_desc")}</div>
          </div>
          <Switch
            checked={!!settings.notify_on_sonic_fire}
            onChange={(v) => patch({ notify_on_sonic_fire: v })}
          />
        </div>
        <div className={styles.toggleRow} style={{ marginTop: 12 }}>
          <div>
            <div className={styles.toggleDesc}>{t("sonic.notify_on_failure_desc")}</div>
          </div>
          <Switch
            checked={!!settings.notify_on_rule_failure}
            onChange={(v) => patch({ notify_on_rule_failure: v })}
          />
        </div>
      </div>

      <div className={styles.settingsCard}>
        <div className={styles.toggleRow}>
          <div>
            <div className={styles.toggleTitle}>{t("sonic.auto_routing")}</div>
            <div className={styles.toggleDesc}>{t("sonic.auto_routing_desc")}</div>
          </div>
          <Switch
            checked={settings.auto_channel_routing_enabled}
            onChange={(v) => patch({ auto_channel_routing_enabled: v })}
          />
        </div>
      </div>

      <div className={styles.settingsCard}>
        <div className={styles.toggleRow}>
          <div>
            <div className={styles.toggleTitle}>{t("sonic.voice_fallback")}</div>
            <div className={styles.toggleDesc}>{t("sonic.voice_fallback_desc")}</div>
          </div>
          <Switch
            checked={settings.voice_fallback_for_unanswered_whatsapp}
            onChange={(v) =>
              patch({ voice_fallback_for_unanswered_whatsapp: v })
            }
          />
        </div>
      </div>

      <div className={styles.settingsCard}>
        <div className={styles.toggleTitle}>{t("sonic.sms_provider_connection")}</div>
        <div className={styles.wabaStatus}>
          {settings.sms_connected ? (
            <BrandTag tone="purple">
              <CheckCircleOutlined /> {t("sonic.connected")}
            </BrandTag>
          ) : (
            <BrandTag tone="pink">
              <ExclamationCircleOutlined /> {t("sonic.not_connected")}
            </BrandTag>
          )}
          <MessageOutlined className={styles.settingsPhoneIcon} />
          <span className={styles.wabaId}>{t("sonic.sms_provider_placeholder")}</span>
        </div>
        <div className={styles.wabaActions}>
          {settings.sms_connected ? (
            <Button danger loading={smsConnecting} onClick={handleSmsDisconnect}>
              {t("sonic.disconnect")}
            </Button>
          ) : (
            <Button type="primary" loading={smsConnecting} onClick={handleSmsConnect}>
              {t("sonic.connect_sms_provider")}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.settingsCard}>
        <div className={styles.toggleRow}>
          <div>
            <div className={styles.toggleTitle}>{t("sonic.sms_fallback")}</div>
            <div className={styles.toggleDesc}>{t("sonic.sms_fallback_desc")}</div>
          </div>
          <Switch
            checked={!!settings.sms_fallback_for_unanswered_whatsapp}
            onChange={(v) => patch({ sms_fallback_for_unanswered_whatsapp: v })}
          />
        </div>
        {settings.sms_fallback_for_unanswered_whatsapp && (
          <div className={styles.nestedField}>
            <label className={styles.fieldLabel}>{t("sonic.fallback_delay")}</label>
            <InputNumber
              min={1}
              max={1440}
              value={settings.sms_fallback_delay_minutes != null ? settings.sms_fallback_delay_minutes : 30}
              onChange={(v) => patch({ sms_fallback_delay_minutes: v || 30 })}
              addonAfter={t("sonic.fallback_delay_unit")}
              style={{ width: 200 }}
            />
          </div>
        )}
      </div>

      <div className={styles.settingsCard} ref={complianceRef}>
        <div className={styles.toggleTitle}>{t("sonic.compliance_section")}</div>
        <p className={styles.fieldHelper}>{t("sonic.compliance_section_desc")}</p>

        <label className={styles.fieldLabel}>{t("sonic.consent_text")}</label>
        <TextArea
          rows={4}
          value={settings.consent_text || ""}
          placeholder={t("sonic.consent_text_ph")}
          onChange={(e) => patch({ consent_text: e.target.value })}
        />
        <p className={styles.fieldHelper}>{t("sonic.consent_text_helper")}</p>

        <label className={styles.fieldLabel}>{t("sonic.opt_out_keywords")}</label>
        <Select
          mode="tags"
          style={{ width: "100%", maxWidth: 480 }}
          value={settings.opt_out_keywords || []}
          onChange={(v) =>
            patch({
              opt_out_keywords: (v || []).map((k) => String(k).trim().toUpperCase()).filter(Boolean),
            })
          }
          placeholder={t("sonic.opt_out_keywords_ph")}
        />
        <p className={styles.fieldHelper}>{t("sonic.opt_out_keywords_helper")}</p>

        <label className={styles.fieldLabel}>{t("sonic.call_recording_retention")}</label>
        <Select
          style={{ width: 280 }}
          value={
            settings.call_recording_retention_days === undefined
              ? null
              : settings.call_recording_retention_days
          }
          onChange={(v) => patch({ call_recording_retention_days: v })}
          options={CALL_RETENTION_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
        />
        <p className={styles.fieldHelper}>{t("sonic.retention_legal_warning")}</p>
      </div>

      <Space>
        <Button type="primary" loading={saving} onClick={save}>
          {t("sonic.save_settings")}
        </Button>
        <Button
          type="link"
          onClick={() => history.push("/console/sonic?tab=conversations")}
        >
          {t("sonic.view_audit_log")}
        </Button>
      </Space>
      </div>
    </div>
  );
};

export default SettingsTab;
